import * as React from 'react';

import Imgix from 'js/components/Imgix';
import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { TrackedA } from 'bundles/page/components/TrackedLink2';
import CourseTotalRatings from 'bundles/video-logged-out-page/components/course-content/CourseTotalRatings';
import TryCourseButton from 'bundles/video-logged-out-page/components/course-content/TryCourseButton';
import type { Partner } from 'bundles/video-logged-out-page/types/vlpSharedTypes';

import _t from 'i18n!nls/video-logged-out-page';

import 'css!bundles/accomplishments/components/shared/__styles__/ProductOverviewSnippet';

type ProductOverviewSnippetProps = {
  id?: string;
  name?: string;
  productLink?: string;
  ratings?: any;
  partners?: Array<any>;
  isCertificateOwner?: boolean;
  isS12n?: boolean;
  enrollmentCount?: number;
};

type TitleHeaderProps = {
  productLink: string;
  name?: string;
};

type LogoContainerProps = {
  partner: Partner;
  logoSrc: string;
  productLink: string;
};

type PartnerNameProps = { productLink: string; partnerName: string };

const LOGO_HEIGHT = 80;
const LOGO_WIDTH = 80;

const LogoContainer: React.SFC<LogoContainerProps> = ({ partner, logoSrc, productLink }) => {
  if (!partner || !productLink || !logoSrc) {
    return null;
  }

  return (
    <div className="partner-logo-container">
      <TrackedA
        trackingName="product_university_logo"
        className="nostyle"
        href={productLink}
        rel="noopener noreferrer"
        target="_blank"
        data-e2e="partner-logo-link"
      >
        <Imgix
          src={logoSrc}
          key={partner.id}
          alt={partner.name}
          maxHeight={LOGO_HEIGHT}
          maxWidth={LOGO_WIDTH}
          className="creator-logo"
        />
      </TrackedA>
    </div>
  );
};

const TitleHeader: React.SFC<TitleHeaderProps> = ({ productLink, name }) => {
  if (!name || !productLink) {
    return null;
  }

  return (
    <div className="course-name-container flex-1" data-e2e="course-name">
      <h3 className="course-name-header">
        <TrackedA href={productLink} trackingName="product_link" className="nostyle">
          {name}
        </TrackedA>
      </h3>
    </div>
  );
};

const PartnerName: React.SFC<PartnerNameProps> = ({ productLink, partnerName }) => {
  if (!partnerName || !productLink) {
    return null;
  }

  return (
    <span className="partner-desc flex-2">
      <TrackedA href={productLink} className="nostyle" trackingName="product_university_name">
        {partnerName}
      </TrackedA>
    </span>
  );
};

const ProductOverviewSnippet: React.FunctionComponent<ProductOverviewSnippetProps> = ({
  ratings,
  partners,
  productLink,
  name,
  isCertificateOwner,
  isS12n,
  enrollmentCount,
}) => {
  const partner = partners && partners[0];
  const { name: partnerName = '', squareLogo: partnerSquareLogo = '' } = partner;
  const logoSrc = partnerSquareLogo;
  const tryCourseButtonMessage = <FormattedMessage message={_t('Enroll for Free')} />;

  if (!productLink) {
    return null;
  }

  return (
    <div className="rc-ProductOverviewSnippet horizontal-box">
      <div className="horizontal-box product-info-section-container">
        <LogoContainer partner={partner} logoSrc={logoSrc} productLink={productLink} />
        <div className="vertical-box info-content">
          <TitleHeader productLink={productLink} name={name} />
          <PartnerName productLink={productLink} partnerName={partnerName} />
          {ratings && isS12n && (
            <CourseTotalRatings
              productLink={productLink}
              courseEnrollmentCount={enrollmentCount}
              ratings={ratings}
              totalReviewCount={Number(ratings.ratingCount)}
              a11yIdentifier="ProductOverview"
            />
          )}
          {ratings && !isS12n && (
            <CourseTotalRatings
              productLink={productLink}
              courseEnrollmentCount={enrollmentCount}
              ratings={ratings}
              totalReviewCount={Number(ratings.ratingCount)}
              a11yIdentifier="ProductOverview"
            />
          )}
        </div>
      </div>
      {productLink && !isCertificateOwner && (
        <div className="product-info-buttons">
          <TryCourseButton
            productLink={productLink}
            buttonClass="try-course"
            customMessage={tryCourseButtonMessage}
            sectionName="cta_banner"
            buttonName="enroll"
          />
        </div>
      )}
    </div>
  );
};

export default ProductOverviewSnippet;
